/**
 * Подготовка данных для переименований в карточке «Состав и названия графиков».
 *
 * Переименование — настройка показа: сам пакет узлов не меняется, а сервер подставляет новые
 * подписи везде, где их видит пользователь. Каталог отдаёт обе подписи — эффективную (`title`,
 * `label`) и пакета (`defaultTitle`, `defaultLabel`), — поэтому текущее переименование здесь
 * восстанавливается сравнением, без отдельного запроса.
 *
 * Модулем, а не внутри компонента, по той же причине, что и `nodeChartVisibility.js`:
 * компоненты тестами не покрываются.
 */

/** Максимальная длина подписи — та же, что проверяет сервер. */
export const MAX_LABEL_LENGTH = 200;

/**
 * Ключи полей ввода. JSON, а не склейка через разделитель: коды значений приходят из пакета
 * и могут содержать любой символ.
 */
export const labelFieldKey = {
  chart: (chartId) => JSON.stringify(['chart', chartId]),
  value: (chartId, code) => JSON.stringify(['value', chartId, code]),
  form: (nodeId) => JSON.stringify(['form', nodeId])
};

/** Подпись в том виде, в каком её хранит сервер: без краевых пробелов, пустая — `null`. */
export const normalizeLabelInput = (value) => {
  const trimmed = (value ?? '').trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * Что отправить на сервер. Совпадение с подписью пакета — это тоже сброс: хранить копию
 * исходника незачем, а после перезаливки пакета с новой подписью она бы её перебивала.
 */
export const toStoredLabel = (value, defaultLabel) => {
  const normalized = normalizeLabelInput(value);
  return normalized === null || normalized === defaultLabel ? null : normalized;
};

const overrideOf = (label, defaultLabel) =>
  defaultLabel != null && label != null && label !== defaultLabel ? label : '';

/**
 * Значения карточки, которые можно переименовать: классы и метки (`options`), а у чек-листа —
 * пункты (`items`). У boolean-карточек обоих списков нет — «Да/Нет» не переименовываются.
 */
export const valueLabelRows = (chart) => {
  const source = chart?.options?.length ? chart.options : (chart?.items ?? []);
  return source.map((option) => ({
    code: option.code,
    label: option.label,
    defaultLabel: option.defaultLabel ?? option.label
  }));
};

/** Узлы-формы чек-листа: их название показывают селектор «Форма оценки» и карточка звонка. */
export const checklistForms = (catalog) =>
  (catalog?.nodes ?? []).filter((node) => node.isChecklistForm === true);

/**
 * Текущие переименования по каталогу: ключ поля → введённое значение (пустая строка — не
 * переименовано).
 */
export const initialLabelOverrides = (catalog) => {
  const overrides = {};

  for (const chart of catalog?.charts ?? []) {
    overrides[labelFieldKey.chart(chart.id)] = overrideOf(chart.title, chart.defaultTitle);

    for (const row of valueLabelRows(chart)) {
      overrides[labelFieldKey.value(chart.id, row.code)] = overrideOf(row.label, row.defaultLabel);
    }
  }

  for (const node of checklistForms(catalog)) {
    overrides[labelFieldKey.form(node.nodeId)] = overrideOf(node.title, node.defaultTitle);
  }

  return overrides;
};

/** Сколько значений карточки переименовано — для подписи свёрнутого списка. */
export const countRenamedValues = (chart, overrides) =>
  valueLabelRows(chart).filter(
    (row) => normalizeLabelInput(overrides?.[labelFieldKey.value(chart.id, row.code)]) !== null
  ).length;

/**
 * Скрытые с графика значения по каталогу: id карточки → коды. Только у карточек, где скрытие
 * поддерживается (`canHideValues` — распределения столбцами и кольцом, без воронки): у
 * остальных сервер набор не применяет, и галка там ничего бы не значила.
 */
export const initialHiddenValues = (catalog) => {
  const hidden = {};

  for (const chart of catalog?.charts ?? []) {
    if (chart.canHideValues === true && (chart.hiddenValues?.length ?? 0) > 0) {
      hidden[chart.id] = [...chart.hiddenValues];
    }
  }

  return hidden;
};

export const isValueHidden = (hidden, chartId, code) => (hidden?.[chartId] ?? []).includes(code);

/** Новое состояние после переключения галки — исходное не меняется, опустевшая карточка уходит. */
export const withValueVisibility = (hidden, chartId, code, visible) => {
  const codes = (hidden?.[chartId] ?? []).filter((item) => item !== code);
  if (!visible) {
    codes.push(code);
  }

  const next = { ...hidden };
  if (codes.length === 0) {
    delete next[chartId];
  } else {
    next[chartId] = codes;
  }

  return next;
};

/** Сколько значений карточки скрыто — для подписи свёрнутого списка. */
export const countHiddenValues = (chart, hidden) =>
  chart?.canHideValues === true
    ? valueLabelRows(chart).filter((row) => isValueHidden(hidden, chart.id, row.code)).length
    : 0;
